import { STATE_VERSION } from '@orb/sim';
import { ERROR, PROTOCOL_VERSION, ProtocolError, decodeServer, encodeClient, type ClientMessage, type ServerMessage } from '@orb/protocol';
import { OPEN, hostEnv, type Env, type SocketLike } from './env.ts';

export type LinkState = 'connecting' | 'open' | 'offline' | 'closed';

export interface LinkOptions {
  url: string;
  /** A session token from an earlier connection (resumed by the server from stage 7). */
  session?: string | null;
  /** The account's session token (`/auth/*`); without one the server may treat us as a guest of this connection only. */
  auth?: string | null;
  env?: Env;
  /** Reconnect backoff: base · 2^attempt, capped, with ±50 % jitter. */
  backoffBase?: number;
  backoffMax?: number;
  pingEvery?: number;
  /** Silence after which the connection counts as dead (turns arrive 20 times a second). */
  deadAfter?: number;
}

/** Errors after which reconnecting cannot help. */
const FATAL = new Set<number>([ERROR.version_mismatch, ERROR.unauthorized]);

/**
 * One logical connection to the game server: hello and session, reconnects with exponential
 * backoff, and an application ping for the round-trip time (smoothed like TCP's SRTT/RTTVAR).
 */
export class Link {
  state: LinkState = 'connecting';
  session: string | null;
  auth: string | null;
  /** Smoothed round trip and its mean deviation, ms (NaN until the first pong). */
  rtt = NaN;
  rttVar = 0;
  onMessage: (m: ServerMessage) => void = () => {};
  onState: (s: LinkState) => void = () => {};
  /** Called once the server welcomed us (each reconnect too); `resumed`: it put us back in our seat. */
  onOpen: (resumed: boolean) => void = () => {};
  /** Called with each new session token, to keep it across reloads. */
  onSession: (token: string) => void = () => {};
  private ws: SocketLike | null = null;
  private attempt = 0;
  private retry: unknown = null;
  private pinger: unknown = null;
  private heardAt = 0;
  private readonly env: Env;
  private readonly o: Required<Omit<LinkOptions, 'session' | 'auth' | 'env'>>;

  constructor(opts: LinkOptions) {
    this.env = opts.env ?? hostEnv();
    this.session = opts.session ?? null;
    this.auth = opts.auth ?? null;
    this.o = { url: opts.url, backoffBase: opts.backoffBase ?? 500, backoffMax: opts.backoffMax ?? 10000, pingEvery: opts.pingEvery ?? 1000, deadAfter: opts.deadAfter ?? 4000 };
  }

  connect() {
    if (this.state === 'closed') return;
    this.setState('connecting');
    const ws = this.ws = this.env.socket(this.o.url);
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => this.raw({ type: 'hello', version: PROTOCOL_VERSION, build: STATE_VERSION, token: this.session, auth: this.auth });
    ws.onmessage = (e) => {
      this.heardAt = this.env.now();
      let m: ServerMessage;
      try { m = decodeServer(new Uint8Array(e.data as ArrayBuffer)); } catch (err) {
        if (err instanceof ProtocolError) { ws.close(ERROR.bad_message, 'bad message'); return; }
        throw err;
      }
      this.receive(m);
    };
    ws.onclose = (e) => this.dropped(ws, e.code);
    ws.onerror = () => {};
  }

  private receive(m: ServerMessage) {
    if (m.type === 'welcome') {
      this.session = m.session; this.attempt = 0;
      this.onSession(m.session);
      this.setState('open'); this.ping(); this.onOpen(m.resumed);
      return;
    }
    if (m.type === 'pong') {
      const sample = this.env.now() - m.time;
      if (sample >= 0) {
        if (Number.isNaN(this.rtt)) { this.rtt = sample; this.rttVar = sample / 2; }
        else { this.rttVar += (Math.abs(sample - this.rtt) - this.rttVar) / 4; this.rtt += (sample - this.rtt) / 8; }
      }
      return;
    }
    if (m.type === 'error' && FATAL.has(m.code)) this.close();
    this.onMessage(m);
  }

  private ping() {
    if (this.state !== 'open') return;
    // a network that silently stopped never closes the socket by itself: give up on it and reconnect
    if (this.env.now() - this.heardAt > this.o.deadAfter) {
      const ws = this.ws;
      if (ws) { ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null; try { ws.close(4000, 'silent'); } catch { /* already gone */ } this.dropped(ws, 1006); }
      return;
    }
    this.raw({ type: 'ping', time: this.env.now() });
    this.pinger = this.env.setTimeout(() => this.ping(), this.o.pingEvery);
  }

  private dropped(ws: SocketLike, code: number) {
    if (ws !== this.ws) return;
    this.ws = null;
    if (this.pinger !== null) { this.env.clearTimeout(this.pinger); this.pinger = null; }
    if (this.state === 'closed' || FATAL.has(code)) { this.setState('closed'); return; }
    this.setState('offline');
    const delay = Math.min(this.o.backoffMax, this.o.backoffBase * 2 ** this.attempt++) * (0.5 + this.env.random() * 0.5);
    this.retry = this.env.setTimeout(() => { this.retry = null; this.connect(); }, delay);
  }

  /** Where we connect; changed by `redial` (another server instance). */
  get url() { return this.o.url; }

  /** Reconnect to this address from now on (the current socket stays). */
  retarget(url: string) { this.o.url = url; }

  /**
   * Move to another address now (the room is on another server instance): the current socket is
   * dropped quietly and a new one opens at once, with a fresh hello.
   */
  redial(url: string) {
    if (this.state === 'closed') return;
    this.o.url = url;
    const ws = this.ws; this.ws = null;
    if (this.pinger !== null) { this.env.clearTimeout(this.pinger); this.pinger = null; }
    if (this.retry !== null) { this.env.clearTimeout(this.retry); this.retry = null; }
    if (ws) { ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null; try { ws.close(1000, 'moving'); } catch { /* already gone */ } }
    this.attempt = 0;
    this.connect();
  }

  /** Send if open; returns whether it went out. */
  send(m: ClientMessage): boolean {
    if (this.state !== 'open') return false;
    return this.raw(m);
  }

  private raw(m: ClientMessage) {
    if (!this.ws || this.ws.readyState !== OPEN) return false;
    this.ws.send(encodeClient(m));
    return true;
  }

  close() {
    this.setState('closed');
    if (this.retry !== null) this.env.clearTimeout(this.retry);
    if (this.pinger !== null) this.env.clearTimeout(this.pinger);
    const ws = this.ws; this.ws = null;
    try { ws?.close(1000, 'bye'); } catch { /* already closed */ }
  }

  private setState(s: LinkState) { if (s === this.state) return; this.state = s; this.onState(s); }
}
